// /src/components/Header.jsx
import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { signOut } from "firebase/auth";
import { auth } from "../firebase";
import { useUIVersion } from "../contexts/UIVersionContext";
import "./header.css";
import wbLogo from "../images/wealthbuilderslogo.png"; // fallback local logo

const LOGO_URL =
  "https://firebasestorage.googleapis.com/v0/b/wealthbuilders-crm-9c323.firebasestorage.app/o/fa509ca3-1165-43d5-b075-f174c232cb04.png?alt=media&token=0f5855f4-8176-47ca-b842-6d7d1301b939";

function Header({ onProfileClick, onUI20SettingsClick, user }) {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();
  const { previewRole, isPreviewMode } = useUIVersion();

  // Fallback to localStorage if user prop not passed
  let u = user;
  if (!u) {
    try {
      u = JSON.parse(localStorage.getItem("authUser") || "null");
    } catch {
      u = null;
    }
  }
  const firstName = (u?.name || u?.fullName || u?.displayName || "User").split(
    " "
  )[0];

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };

    if (dropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [dropdownOpen]);

  const handleProfileClick = () => {
    setDropdownOpen(false);
    navigate("/profile");
  };

  const handleLogout = async () => {
    try {
      // Sign out from Firebase
      await signOut(auth);

      // Clear ONLY auth-related data from localStorage
      // Preserve tracker data and other app state
      localStorage.removeItem("authUser");
      localStorage.removeItem("wb.plan");
      localStorage.removeItem("wb.userId");
      localStorage.removeItem("wb.userEmail");
      localStorage.removeItem("wb.userName");

      // Close the dropdown menu
      setDropdownOpen(false);

      // Navigate to login page
      navigate("/login");
    } catch (error) {
      console.error("Error during logout:", error);
      // Even if Firebase signOut fails, clear local data and redirect
      localStorage.removeItem("authUser");
      localStorage.removeItem("wb.plan");
      localStorage.removeItem("wb.userId");
      localStorage.removeItem("wb.userEmail");
      localStorage.removeItem("wb.userName");
      navigate("/login");
    }
  };

  return (
    <header className="crm-header white-header">
      <div className="crm-header-left">
        <div className="brand">
          <img
            src={LOGO_URL}
            alt="Wealth Builders"
            className="brand-logo"
            loading="eager"
            decoding="async"
            onError={(e) => {
              // graceful fallback to bundled asset if the remote image 404s
              if (e.currentTarget.src !== wbLogo) e.currentTarget.src = wbLogo;
            }}
          />
        </div>
      </div>

      <div className="crm-header-right">
        {isPreviewMode && (
          <button
            className="preview-mode-badge"
            onClick={onUI20SettingsClick}
            title="Preview mode active. Click to change."
          >
            👁️ Viewing as: <strong>{previewRole}</strong>
          </button>
        )}
        <span className="welcome">Welcome, {firstName}</span>
        {onUI20SettingsClick && (
          <button
            className="btn ui20-settings-btn"
            onClick={onUI20SettingsClick}
            title="Open UI 2.0 Settings"
          >
            UI 2.0
          </button>
        )}
        <div className="profile-dropdown-container" ref={dropdownRef}>
          <button
            className={`btn profile-btn ${dropdownOpen ? "active" : ""}`}
            onClick={() => setDropdownOpen(!dropdownOpen)}
            title="User menu"
          >
            Profile
          </button>
          {dropdownOpen && (
            <div className="profile-dropdown">
              <div className="profile-dropdown-header">
                <div className="profile-dropdown-name">{firstName}</div>
                <div className="profile-dropdown-email">
                  {u?.email || "user@example.com"}
                </div>
              </div>
              <div className="profile-dropdown-divider" />
              <button
                className="profile-dropdown-item"
                onClick={handleProfileClick}
              >
                <span>👤</span> Profile
              </button>
              <div className="profile-dropdown-divider" />
              <button
                className="profile-dropdown-item danger"
                onClick={handleLogout}
              >
                <span>🚪</span> Sign out
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}

export default Header;
export { Header };
export { Header as HeaderNamed };
